import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Horizontal split with a draggable divider. State is local — the caller
 * passes initial widths and min/max for each side, we manage the rest.
 *
 * Layout: a leading pane (left/right) gets a fixed pixel width; the
 * remaining flex child consumes the rest.
 */
export interface SplitPaneProps {
  side: "left" | "right";
  defaultSize: number;
  min: number;
  max: number;
  storageKey?: string;
  children: [React.ReactNode, React.ReactNode];
}

export function SplitPane({
  side,
  defaultSize,
  min,
  max,
  storageKey,
  children,
}: SplitPaneProps) {
  const [size, setSize] = useState<number>(() => {
    if (storageKey) {
      const raw = typeof localStorage !== "undefined" ? localStorage.getItem(storageKey) : null;
      const n = raw ? Number.parseInt(raw, 10) : Number.NaN;
      if (Number.isFinite(n) && n >= min && n <= max) return n;
    }
    return defaultSize;
  });
  const draggingRef = useRef(false);
  const startRef = useRef({ x: 0, size: 0 });

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, String(size));
    } catch {
      // ignore quota errors
    }
  }, [size, storageKey]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      draggingRef.current = true;
      startRef.current = { x: e.clientX, size };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      const onMove = (ev: MouseEvent) => {
        if (!draggingRef.current) return;
        const delta = ev.clientX - startRef.current.x;
        const next = side === "left"
          ? startRef.current.size + delta
          : startRef.current.size - delta;
        setSize(Math.min(max, Math.max(min, next)));
      };
      const onUp = () => {
        draggingRef.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [size, side, min, max],
  );

  const handle = (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={size}
      aria-valuemin={min}
      aria-valuemax={max}
      onMouseDown={onMouseDown}
      style={{
        width: 4,
        background: "var(--border-default)",
        cursor: "col-resize",
        flexShrink: 0,
        transition: "background 80ms",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = "var(--accent-blue)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = "var(--border-default)";
      }}
    />
  );

  const fixedPane = (
    <div style={{ width: size, flexShrink: 0, display: "flex", minHeight: 0 }}>
      {side === "left" ? children[0] : children[1]}
    </div>
  );

  const flexPane = (
    <div style={{ flex: 1, minWidth: 0, display: "flex", minHeight: 0 }}>
      {side === "left" ? children[1] : children[0]}
    </div>
  );

  return (
    <div style={{ flex: 1, minWidth: 0, display: "flex", minHeight: 0 }}>
      {side === "left" ? (
        <>
          {fixedPane}
          {handle}
          {flexPane}
        </>
      ) : (
        <>
          {flexPane}
          {handle}
          {fixedPane}
        </>
      )}
    </div>
  );
}
